import React, { useState } from 'react';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { FormattedMessage } from 'react-intl';

import StatusBadge, { getStatusIcon } from './StatusBadge';
import { useModal } from 'mui-modal-provider';
import CommentAndStatusUpdate from '../../Posts/Shared/CommentAndStatusUpdate/CommentAndStatusUpdate';
import useApiQuery from '../../../Hooks/useApiQuery';
import queriesConfig from '../../../Services/queriesConfig';
import { Post } from '../../../Utils/Types/Posts';
import { PostStatus } from '../../../Utils/Types/PostStatuses';
import PostStatusHelpDialog from '../../Posts/Shared/PostStatus/PostStatusHelpDialog';

interface StatusBadgeMenuProps {
  status: PostStatus;
  size?: 'medium' | 'small';
  post: Post;
  canUpdateStatus: boolean;
  typographyComponent?: string;
  availableStatuses?: PostStatus[];
}

function StatusBadgeMenu({
  status,
  size = 'small',
  availableStatuses = [],
  post,
  canUpdateStatus,
  ...props
}: StatusBadgeMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { showModal } = useModal();

  const { data: fetchedStatuses = [] } = useApiQuery({
    query: queriesConfig.getPostStatus(post.id),
    options: { enabled: open },
  });

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (canUpdateStatus) {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [isDetailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const handleDetailsDialogClose = () => {
    setDetailsDialogOpen(false);
  };

  const handleStatusSelect = (statusKeyword: string) => {
    handleClose();

    const postsArray = post ? [post] : [{ id: post.id }];

    const modal = showModal(CommentAndStatusUpdate, {
      open: true,
      posts: postsArray,
      preSelectedStatus: statusKeyword,
      onSubmit: () => {
        modal.destroy();
      },
      onClose: () => {
        modal.destroy();
      },
    });
  };

  const allStatuses = fetchedStatuses.length > 0 ? fetchedStatuses : availableStatuses;

  if (!canUpdateStatus) {
    return (
      <>
        <StatusBadge status={status} size={size} onClick={setDetailsDialogOpen} {...props} />
        {allStatuses.length > 0 && (
          <PostStatusHelpDialog
            currentStatus={post.current_status}
            statuses={allStatuses}
            open={isDetailsDialogOpen}
            onClose={handleDetailsDialogClose}
          />
        )}
      </>
    );
  }

  return (
    <>
      <StatusBadge
        status={status}
        size={size}
        onClick={handleClick}
        endIcon={
          <ArrowDropDownIcon
            fontSize="small"
            style={{
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        }
        {...props}
      />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            'aria-labelledby': 'status-badge-button',
          },
        }}
      >
        {allStatuses &&
          allStatuses.map((statusItem) => (
            <MenuItem
              key={statusItem.keyword}
              onClick={() => handleStatusSelect(statusItem.keyword)}
            >
              <ListItemIcon>{getStatusIcon(statusItem.keyword, 'action')}</ListItemIcon>
              <ListItemText>
                {statusItem.title || (
                  <FormattedMessage id={`POSTS.STATUS.${statusItem.keyword.toUpperCase()}`} />
                )}
              </ListItemText>
            </MenuItem>
          ))}
      </Menu>
    </>
  );
}

export default StatusBadgeMenu;
